/**
 * 13L Arc Hero Slider — continuous arc marquee with drag and hover lift.
 *
 * Cards sit on a flex track that slides via a single rAF loop. Each frame,
 * every card's rotation and vertical drop are recomputed from its current
 * horizontal distance to the viewport center, as if the cards sat on the rim
 * of a wheel whose center is far below the section. Dragging moves the same
 * offset, so the arc stays exact during autoplay, drag and inertia.
 *
 * Hover: hovering a card smoothly lifts it upward and raises its z-index so
 * it no longer overlaps neighbours.
 */
(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-section-type="13l-arc-hero-slider"]';
  const MAX_ANGLE = 48;        // deg — off-screen cards stop rotating past this
  const CLICK_SUPPRESS_PX = 5;
  const RESUME_DELAY_MS = 1500;
  const MAX_INERTIA = 2;       // px per ms
  const HOVER_LIFT_PX = 22;    // how many px a hovered card rises
  const HOVER_LERP = 0.1;      // interpolation speed per frame (~60 fps)
  const SNAP_LERP = 0.18;      // ease strength when settling a card to center
  const SNAP_PROJECT_MS = 120; // how far a release velocity carries the snap

  const registry = new WeakMap();
  const IS_DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  /**
   * Outer curtain slideshow (Fornasetti-style): cells are stacked layers.
   * The current cell sits on top clipped to (100% - peek) so the cell
   * underneath always shows a strip on the right. Navigation slides the
   * current cell out to the left like a curtain; the revealed cell does
   * not move. Independent from the arc marquee engine so it still works
   * when the section has no card blocks. Returns the curtain API or null.
   */
  function createCurtain(root, onReveal) {
    const carouselEl = root.querySelector('[data-arc-carousel]');
    if (!carouselEl) return null;
    const cells = Array.from(carouselEl.querySelectorAll('.arc-hero-carousel__cell'));
    if (cells.length < 2) return null;

    const autoplayTiming = parseInt(root.dataset.autoplayTiming, 10) || 0;
    const duration = (parseFloat(root.dataset.curtainDuration) || 2) * 1000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let current = 0;
    let animating = false;
    let autoplayTimer = null;
    let settleTimer = null;
    let revealTimer = null;
    let destroyed = false;
    let dots = [];

    const listeners = new AbortController();
    const lsignal = listeners.signal;

    function clearStates(cell) {
      cell.classList.remove('is-current', 'is-under', 'is-leaving');
    }

    function applyStates() {
      const under = (current + 1) % cells.length;
      cells.forEach(function (cell, i) {
        clearStates(cell);
        if (i === current) cell.classList.add('is-current');
        else if (i === under) cell.classList.add('is-under');
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === current);
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      if (onReveal) onReveal();
    }

    function settle(target) {
      clearTimeout(settleTimer);
      clearTimeout(revealTimer);
      settleTimer = null;
      revealTimer = null;
      current = target;
      animating = false;
      applyStates();
    }

    function goTo(target) {
      if (destroyed || animating) return;
      target = ((target % cells.length) + cells.length) % cells.length;
      if (target === current) return;

      if (reducedMotion.matches) {
        settle(target);
        return;
      }

      animating = true;
      const leaving = cells[current];
      // Place the target underneath at full width, everything else below it.
      cells.forEach(function (cell, i) {
        if (i === current) return;
        clearStates(cell);
        if (i === target) cell.classList.add('is-under');
      });
      if (onReveal) onReveal();

      // Slide the curtain out to the left; the cell keeps its peek clip.
      const onDone = function () { settle(target); };
      leaving.addEventListener('transitionend', function handler(e) {
        if (e.target !== leaving || e.propertyName !== 'transform') return;
        leaving.removeEventListener('transitionend', handler);
        if (animating) onDone();
      });
      // Fallback in case transitionend never fires (e.g. tab hidden).
      settleTimer = setTimeout(function () {
        if (animating) onDone();
      }, duration + 150);

      // Early arrival: while the curtain is still finishing, promote the
      // target to current so its clip starts revealing the upcoming slide's
      // peek, and place that upcoming slide underneath.
      revealTimer = setTimeout(function () {
        if (!animating || destroyed) return;
        const upcoming = (target + 1) % cells.length;
        cells[target].classList.remove('is-under');
        cells[target].classList.add('is-current');
        if (upcoming !== target && cells[upcoming] !== leaving) {
          cells[upcoming].classList.add('is-under');
        }
      }, duration * 0.6);

      void leaving.offsetWidth;
      leaving.classList.add('is-leaving');
    }

    function next() {
      goTo(current + 1);
    }

    /* ---- Autoplay ---- */
    function stopAutoplay() {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplayTiming || destroyed) return;
      autoplayTimer = setInterval(function () {
        if (!document.hidden) next();
      }, autoplayTiming);
    }

    carouselEl.addEventListener('mouseenter', stopAutoplay, { signal: lsignal });
    carouselEl.addEventListener('mouseleave', startAutoplay, { signal: lsignal });

    /* ---- Swipe navigation ----
       The arc marquee stops propagation of its own drags on desktop, so
       these handlers only see swipes on banner slides (and, on mobile,
       anywhere — the marquee yields its drag there). */
    let swipeId = null;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeDx = 0;
    let swipeGesture = null;
    let suppressSwipeClick = false;

    carouselEl.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      swipeId = e.pointerId;
      swipeStartX = e.clientX;
      swipeStartY = e.clientY;
      swipeDx = 0;
      swipeGesture = null;
    }, { signal: lsignal });

    carouselEl.addEventListener('pointermove', function (e) {
      if (e.pointerId !== swipeId) return;
      const dx = e.clientX - swipeStartX;
      const dy = e.clientY - swipeStartY;
      if (swipeGesture === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        swipeGesture = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (swipeGesture === 'v') {
          swipeId = null;
          return;
        }
      }
      swipeDx = dx;
    }, { signal: lsignal });

    function endSwipe(e) {
      if (e.pointerId !== swipeId) return;
      swipeId = null;
      if (swipeGesture !== 'h') return;
      swipeGesture = null;
      if (Math.abs(swipeDx) > 60) {
        suppressSwipeClick = true;
        goTo(swipeDx < 0 ? current + 1 : current - 1);
        startAutoplay();
      }
    }
    carouselEl.addEventListener('pointerup', endSwipe, { signal: lsignal });
    carouselEl.addEventListener('pointercancel', endSwipe, { signal: lsignal });

    carouselEl.addEventListener('click', function (e) {
      if (suppressSwipeClick) {
        suppressSwipeClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true, signal: lsignal });

    /* ---- Pagination dots ---- */
    let dotsList = null;
    if (root.dataset.showDots === 'true') {
      dotsList = document.createElement('ol');
      dotsList.className = 'arc-hero-carousel__dots';
      cells.forEach(function (cell, i) {
        const item = document.createElement('li');
        item.className = 'arc-hero-carousel__dots-item';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'arc-hero-carousel__dot';
        button.setAttribute('aria-label', 'Slide ' + (i + 1));
        button.addEventListener('click', function () {
          goTo(i);
          startAutoplay();
        });
        item.appendChild(button);
        dotsList.appendChild(item);
        dots.push(button);
      });
      carouselEl.appendChild(dotsList);
    }

    // Warm up every cell's media so hidden slides are ready before their
    // peek strip appears (avoids a white flash): decode all images, set
    // videos to preload and wait for their first frames. Autoplay only
    // starts once everything is ready (capped at 5s so it never stalls).
    function warmUp() {
      const jobs = [];
      cells.forEach(function (cell) {
        cell.querySelectorAll('img').forEach(function (img) {
          if (typeof img.decode === 'function') {
            jobs.push(img.decode().catch(function () {}));
          }
        });
        cell.querySelectorAll('video').forEach(function (video) {
          video.setAttribute('preload', 'auto');
          if (video.readyState < 3) {
            jobs.push(new Promise(function (resolve) {
              video.addEventListener('canplay', resolve, { once: true });
              setTimeout(resolve, 4000);
            }));
          }
        });
      });
      return Promise.race([
        Promise.all(jobs),
        new Promise(function (resolve) { setTimeout(resolve, 5000); })
      ]);
    }

    applyStates();
    carouselEl.classList.add('is-ready');
    warmUp().then(function () {
      if (!destroyed) startAutoplay();
    });

    return {
      select: function (index) { goTo(index); },
      cellIndex: function (cell) { return cells.indexOf(cell); },
      pause: stopAutoplay,
      resume: startAutoplay,
      destroy: function () {
        destroyed = true;
        stopAutoplay();
        clearTimeout(settleTimer);
        clearTimeout(revealTimer);
        listeners.abort();
        cells.forEach(clearStates);
        carouselEl.classList.remove('is-ready');
        if (dotsList) dotsList.remove();
        dots = [];
      }
    };
  }

  function createSlider(root) {
    const slider = root.querySelector('[data-arc-slider]');
    const track = root.querySelector('[data-arc-track]');
    const originals = track ? Array.from(track.querySelectorAll('[data-arc-card]')) : [];

    // No arc cards — still run the outer curtain for the banner slides.
    if (!slider || !track || !originals.length) {
      let curtainOnly = createCurtain(root, null);
      return {
        destroy: function () {
          if (curtainOnly) {
            curtainOnly.destroy();
            curtainOnly = null;
          }
        },
        getCarousel: function () { return curtainOnly; }
      };
    }

    const settings = {
      speed: parseFloat(root.dataset.speed) || 40,
      angleStep: parseFloat(root.dataset.angleStep) || 12,
      arcDepth: (parseFloat(root.dataset.arcDepth) || 100) / 100,
      arc: root.dataset.enableArcCurve !== 'false',
      marquee: root.dataset.enableMarquee === 'true',
      pauseOnHover: root.dataset.pauseOnHover === 'true'
    };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const abort = new AbortController();
    const signal = abort.signal;

    let cards = [];
    let lefts = [];
    let cardWidth = 0;
    let spacing = 0;
    let setWidth = 0;
    let containerWidth = 0;
    let startOffset = 0;
    let offset = 0;

    let rafId = null;
    let lastTime = null;
    let visible = true;
    let hovered = false;   // whole-slider hover (for pauseOnHover)
    let focused = false;
    let dragging = false;
    let cooldown = false;
    let inertia = 0;
    let snapTarget = null;   // offset that centers the nearest card, or null
    let suppressClick = false;
    let resumeTimer = null;
    let resizeTimer = null;

    // Per-card hover lift state
    let hoveredCardEl = null;            // which card the mouse is currently over
    let hoverProgress = [];              // parallel array to `cards`: 0..1

    function autoplayActive() {
      return settings.marquee && !reducedMotion.matches &&
        !hovered && !focused && !dragging && !cooldown;
    }

    function setup() {
      track.querySelectorAll('[data-arc-clone]').forEach((el) => el.remove());

      containerWidth = slider.clientWidth;
      cardWidth = originals[0].offsetWidth;
      spacing = originals.length > 1
        ? originals[1].offsetLeft - originals[0].offsetLeft
        : cardWidth;
      setWidth = originals.length * spacing;

      const centerIndex = Math.floor(originals.length / 2);
      startOffset = containerWidth / 2 - cardWidth / 2 - centerIndex * spacing;
      while (startOffset > 0) startOffset -= setWidth;

      const neededWidth = containerWidth - (startOffset - setWidth);
      const totalSets = Math.max(2, Math.ceil(neededWidth / setWidth) + 1);
      if (!IS_DESIGN_MODE) {
        const fragment = document.createDocumentFragment();
        for (let s = 1; s < totalSets; s++) {
          originals.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-arc-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
            fragment.appendChild(clone);
          });
        }
        track.appendChild(fragment);
      }

      cards = Array.from(track.querySelectorAll('[data-arc-card]'));
      lefts = cards.map((card) => card.offsetLeft);
      hoverProgress = cards.map(() => 0);

      hoveredCardEl = null;
      inertia = 0;
      offset = startOffset;
      render();
    }

    function wrap() {
      while (offset <= startOffset - setWidth) {
        offset += setWidth;
        if (snapTarget !== null) snapTarget += setWidth;
      }
      while (offset > startOffset) {
        offset -= setWidth;
        if (snapTarget !== null) snapTarget -= setWidth;
      }
    }

    // Offset that centers the card nearest to `fromOffset`. Valid centered
    // offsets repeat every `spacing`, so we round to the closest one.
    function nearestSnap(fromOffset) {
      if (!spacing) return fromOffset;
      const centerOffset = containerWidth / 2 - cardWidth / 2 - lefts[0];
      return centerOffset - Math.round((centerOffset - fromOffset) / spacing) * spacing;
    }

    function render() {
      track.style.transform = 'translate3d(' + offset.toFixed(2) + 'px, 0, 0)';

      const half = containerWidth / 2;
      const stepRad = (settings.angleStep * Math.PI) / 180;
      const radius = spacing / Math.sin(stepRad);

      for (let i = 0; i < cards.length; i++) {
        const center = lefts[i] + offset + cardWidth / 2;
        // Flat mode (arc curve off): no rotation, no drop — a straight
        // horizontal row that still scrolls and drags like the arc.
        let angle = 0;
        let arcDrop = 0;
        if (settings.arc) {
          angle = ((center - half) / spacing) * settings.angleStep;
          if (angle > MAX_ANGLE) angle = MAX_ANGLE;
          else if (angle < -MAX_ANGLE) angle = -MAX_ANGLE;
          arcDrop = radius * (1 - Math.cos((angle * Math.PI) / 180)) * settings.arcDepth;
        }

        // Smooth hover lift interpolation
        const isHoveredCard = !dragging && cards[i] === hoveredCardEl;
        const target = isHoveredCard ? 1 : 0;
        let p = hoverProgress[i];
        p += (target - p) * HOVER_LERP;
        if (p < 0.002) p = 0;
        if (p > 0.998) p = 1;
        hoverProgress[i] = p;

        const finalDrop = arcDrop - p * HOVER_LIFT_PX;
        cards[i].style.transform =
          'translate3d(0, ' + finalDrop.toFixed(2) + 'px, 0) rotate(' + angle.toFixed(2) + 'deg)';

        if (p > 0) {
          cards[i].style.zIndex = String(Math.round(MAX_ANGLE - Math.abs(angle)) + 1 + Math.round(p * 60));
          cards[i].classList.add('is-hovered');
        } else {
          cards[i].style.zIndex = String(Math.round(MAX_ANGLE - Math.abs(angle)) + 1);
          cards[i].classList.remove('is-hovered');
        }
      }
    }

    function tick(time) {
      rafId = null;
      if (!visible || signal.aborted) {
        lastTime = null;
        return;
      }
      const dt = lastTime === null ? 0 : Math.min(time - lastTime, 64);
      lastTime = time;

      if (!dragging) {
        if (snapTarget !== null) {
          // Ease toward the centered offset (frame-rate independent).
          const d = snapTarget - offset;
          if (Math.abs(d) < 0.5) {
            offset = snapTarget;
            snapTarget = null;
          } else {
            offset += d * (1 - Math.pow(1 - SNAP_LERP, dt / 16.67));
          }
        } else if (autoplayActive()) {
          offset -= (settings.speed * dt) / 1000;
        }
      }
      wrap();
      render();
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (IS_DESIGN_MODE || rafId !== null || signal.aborted) return;
      lastTime = null;
      rafId = requestAnimationFrame(tick);
    }

    /* ---- Per-card hover tracking ---- */
    slider.addEventListener('mouseover', (e) => {
      if (dragging) return;
      hoveredCardEl = e.target.closest('[data-arc-card]') || null;
    }, { signal });

    slider.addEventListener('mouseleave', () => {
      hoveredCardEl = null;
    }, { signal });

    /* ---- Drag ---- */
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastMoveTime = 0;
    let totalDx = 0;
    let gesture = null;
    let velocity = 0;

    slider.addEventListener('pointerdown', (e) => {
      // Keep the gesture for the cards — never let the outer slideshow
      // start its own swipe from inside the cards slider.
      e.stopPropagation();
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      startY = e.clientY;
      gesture = null;
      totalDx = 0;
      velocity = 0;
      suppressClick = false;
      lastMoveTime = performance.now();
    }, { signal });

    slider.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointerId) return;
      if (gesture === null) {
        const adx = Math.abs(e.clientX - startX);
        const ady = Math.abs(e.clientY - startY);
        if (adx < 4 && ady < 4) return;
        if (adx <= ady) {
          pointerId = null;
          return;
        }
        gesture = 'h';
        dragging = true;
        hoveredCardEl = null;
        inertia = 0;
        snapTarget = null;
        slider.classList.add('is-dragging');
        slider.setPointerCapture(pointerId);
      }
      const now = performance.now();
      const dx = e.clientX - lastX;
      velocity = dx / Math.max(now - lastMoveTime, 1);
      lastMoveTime = now;
      lastX = e.clientX;
      totalDx += Math.abs(dx);
      offset += dx;
      wrap();
      start();
    }, { signal });

    function endDrag(e) {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      slider.classList.remove('is-dragging');
      if (totalDx > CLICK_SUPPRESS_PX) suppressClick = true;
      // Snap the nearest card to center; a quick flick carries one card over.
      const v = Math.max(-MAX_INERTIA, Math.min(MAX_INERTIA, velocity));
      inertia = 0;
      snapTarget = nearestSnap(offset + v * SNAP_PROJECT_MS);
      cooldown = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        cooldown = false;
        start();
      }, RESUME_DELAY_MS);
      start();
    }
    slider.addEventListener('pointerup', endDrag, { signal });
    slider.addEventListener('pointercancel', endDrag, { signal });

    // Fallback isolation for browsers without PointerEvent.
    slider.addEventListener('mousedown', (e) => e.stopPropagation(), { signal });
    slider.addEventListener('touchstart', (e) => e.stopPropagation(), { signal, passive: true });

    slider.addEventListener('click', (e) => {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true, signal });

    /* ---- Whole-slider pause states (pauseOnHover) ---- */
    if (settings.pauseOnHover) {
      slider.addEventListener('mouseenter', () => { hovered = true; }, { signal });
      slider.addEventListener('mouseleave', () => {
        hovered = false;
        start();
      }, { signal });
    }

    slider.addEventListener('focusin', () => { focused = true; }, { signal });
    slider.addEventListener('focusout', () => {
      focused = false;
      start();
    }, { signal });

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', start, { signal });
    }

    // Observe the arc slider itself (not the section root): when the arc cell
    // is slid out of the outer carousel's clipped viewport it stops
    // intersecting, which pauses the marquee loop.
    const io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) start();
    });
    io.observe(slider);

    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setup();
        start();
      }, 150);
    }, { signal });

    /* ---- Outer curtain slideshow (banner slide cells + arc cell) ---- */
    let curtain = null;

    function destroy() {
      abort.abort();
      io.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      clearTimeout(resumeTimer);
      clearTimeout(resizeTimer);
      if (curtain) {
        curtain.destroy();
        curtain = null;
      }
      track.querySelectorAll('[data-arc-clone]').forEach((el) => el.remove());
      track.style.transform = '';
      originals.forEach((card) => {
        card.style.transform = '';
        card.style.zIndex = '';
        card.classList.remove('is-hovered');
      });
    }

    setup();
    if (IS_DESIGN_MODE) {
      render();
    } else {
      start();
    }
    // Hidden cells keep their layout (visibility-based states), so the arc
    // geometry measured above stays valid — no re-setup needed per slide.
    curtain = createCurtain(root, null);

    return {
      destroy,
      getCarousel: function () { return curtain; }
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SECTION_SELECTOR).forEach((root) => {
      if (registry.has(root)) return;
      const instance = createSlider(root);
      if (instance) registry.set(root, instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll(SECTION_SELECTOR).forEach((root) => {
      const instance = registry.get(root);
      if (instance) {
        instance.destroy();
        registry.delete(root);
      }
    });
  });

  /* ---- Theme editor: sync the curtain with block selection ---- */
  function getCarouselFor(target) {
    const root = target.closest(SECTION_SELECTOR);
    if (!root) return null;
    const instance = registry.get(root);
    return instance ? instance.getCarousel() : null;
  }

  document.addEventListener('shopify:block:select', (e) => {
    const curtain = getCarouselFor(e.target);
    if (!curtain) return;
    // Card/product blocks live inside the arc cell; banner blocks are cells.
    const cell = e.target.closest('.arc-hero-carousel__cell');
    if (cell) {
      const index = curtain.cellIndex(cell);
      if (index !== -1) curtain.select(index);
    }
    curtain.pause();
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const curtain = getCarouselFor(e.target);
    if (curtain) curtain.resume();
  });
})();
